/**
 * `belief.contradicts_canon` (§5.2 Cross-fact patterns; ADR-0005 §8) — a
 * `belief`-owned fact's `(subject, base-predicate)` matches a canon fact's,
 * their validity overlaps, and the values differ.
 *
 * Beliefs are ALLOWED to contradict canon — this is the misinformation layer
 * working as designed (`03 §9`). The advisory is purely informational,
 * surfacing narrative opportunity ("this Viewer holds a false belief about
 * X"). Severity: `info`, GM-only — and the auditor side rail is the GM's
 * authoring perspective, so there's no projection leak.
 *
 * ADR-0005 §8 — belief is the OWNER, not a key suffix: a belief is recognised
 * by `fact.owner.kind === 'belief'` and lives on a `<base>#seg_…` segment chain
 * whose base is recovered by stripping the segment id. A belief that has been
 * CORRECTED collapses into a CLOSED belief segment (a `validity.to` was
 * stamped — "believed until day N"); a closed belief is resolved history, not a
 * live contradiction, so it is skipped.
 */
import { currentFactValue, splitSegmentKey, viewerKey } from '@dl/shared';

import type { RawAdvisory } from '../advisory.js';
import { factRef } from '../advisory.js';
import type { AuditContext } from '../engine.js';

import { validityOverlaps, valuesEqual } from './helpers.js';

export function beliefContradictsCanon(ctx: AuditContext): RawAdvisory[] {
  const out: RawAdvisory[] = [];

  for (const entity of Object.values(ctx.entities)) {
    if (entity.soft_deleted) continue;

    // Collect live canon facts by base predicate for O(1) lookup.
    const canonByBase = new Map<string, { key: string; fact: (typeof entity.facts)[string] }>();
    for (const [factKey, fact] of Object.entries(entity.facts)) {
      if (fact.soft_deleted) continue;
      if (fact.owner.kind !== 'canon') continue;
      const base = splitSegmentKey(factKey).base;
      // First live canon segment per base is enough for the contradiction check
      // (an info advisory; the segment-resolution detail lives in projection).
      if (!canonByBase.has(base)) canonByBase.set(base, { key: factKey, fact });
    }

    for (const [factKey, belief] of Object.entries(entity.facts)) {
      if (belief.soft_deleted) continue;
      if (belief.owner.kind !== 'belief') continue;
      // A CLOSED belief segment (a `validity.to` was stamped) is corrected
      // history ("believed until day N"), not a live contradiction.
      if (belief.validity?.to !== undefined) continue;

      const base = splitSegmentKey(factKey).base;
      const canon = canonByBase.get(base);
      if (canon === undefined) continue;

      if (!validityOverlaps(belief.validity, canon.fact.validity)) continue;

      const beliefValue = currentFactValue(belief);
      const canonValue = currentFactValue(canon.fact);
      if (valuesEqual(beliefValue, canonValue)) continue; // agrees with canon

      out.push({
        severity: 'info',
        rule: 'belief.contradicts_canon',
        message_key: 'auditor.belief.contradicts_canon',
        params: {
          predicate: base,
          viewer: viewerKey(belief.owner.viewer),
        },
        fact_ids: [factRef(entity.id, factKey), factRef(entity.id, canon.key)],
        entity_ids: [entity.id],
      });
    }
  }

  return out;
}
